import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import SchemaOrg from '@/components/base/SchemaOrg';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, getHotComics, getLatestComics, getPopularComics } from '@/mocks/comics';

export default function Home() {
  const { t } = useTranslation();
  const hotComics = getHotComics(6);
  const latestComics = getLatestComics(6);
  const popularComics = getPopularComics(5);

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '韩漫漫画网站 - 首页',
          description: '韩漫漫画网站是免费韩漫与漫画大全的宝库，提供海量在线漫画资源，并持续为您挖掘和推荐好看的韩国漫画。',
          url: window.location.origin + '/',
          isPartOf: { '@type': 'WebSite', name: '韩漫漫画网站', url: window.location.origin },
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[480px] md:h-[620px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=A%20stylized%20artistic%20illustration%20of%20a%20comic%20book%20world%20with%20floating%20manga%20panels%20and%20vibrant%20color%20splashes%20in%20warm%20orange%20pink%20and%20gold%20tones%2C%20abstract%20geometric%20comic%20page%20layouts%20floating%20in%20space%2C%20modern%20digital%20webtoon%20aesthetic%2C%20clean%20and%20artistic%20composition%2C%20no%20text%20or%20characters%20visible&width=1600&height=900&seq=hero-bg-manhwa&orientation=landscape"
              alt="韩漫漫画网站 - 免费韩漫 漫画大全 在线漫画"
              title="韩漫漫画网站 - 免费韩漫,漫画大全,在线漫画,好看的韩国漫画"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-full text-center px-4">
                <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-white mb-4 drop-shadow-lg">
                  韩漫漫画网站
                </h1>
                <p className="text-base md:text-xl text-white/90 mb-4 max-w-2xl mx-auto leading-relaxed">
                  {t('home.hero.subtitle')}
                </p>
                <p className="text-sm md:text-base text-white/70 mb-8 max-w-xl mx-auto">
                  韩漫漫画网站是免费韩漫与漫画大全的宝库，提供海量在线漫画资源，并持续为您挖掘和推荐好看的韩国漫画。
                </p>
                <Link
                  to="/category/all"
                  className="inline-block px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full font-medium text-base transition-all duration-300 whitespace-nowrap"
                >
                  {t('home.hero.cta')}
                </Link>
              </div>
            </div>
          </section>

          {/* Hot Comics Section */}
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-14 md:py-20">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">
                {t('home.hot.title')}
              </h2>
              <p className="text-sm text-foreground-500 mt-2">{t('home.hot.subtitle')}</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {hotComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </section>

          {/* Latest Updates Section */}
          <section className="bg-background-100 py-14 md:py-20">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">
                    {t('home.latest.title')}
                  </h2>
                  <p className="text-sm text-foreground-500 mt-2">{t('home.latest.subtitle')}</p>
                </div>
                <Link
                  to="/category/all"
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap flex items-center gap-1"
                >
                  {t('home.viewAll')} <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                {latestComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            </div>
          </section>

          {/* Category Browse Section */}
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-14 md:py-20">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">
                {t('home.category.title')}
              </h2>
              <p className="text-sm text-foreground-500 mt-2">{t('home.category.subtitle')}</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
              {categories.filter((c) => c.slug !== 'all').map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-3 p-5 rounded-xl bg-background-100 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-full bg-accent-100 group-hover:bg-primary-100 flex items-center justify-center transition-colors">
                    <i className={`${cat.icon} text-xl text-accent-600 group-hover:text-primary-600 transition-colors`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          </section>

          {/* Popular Ranking Section */}
          <section className="bg-background-100 py-14 md:py-20">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">
                  {t('home.ranking.title')}
                </h2>
                <p className="text-sm text-foreground-500 mt-2">{t('home.ranking.subtitle')}</p>
              </div>
              <div className="max-w-3xl mx-auto space-y-3">
                {popularComics.map((comic, index) => (
                  <Link
                    key={comic.id}
                    to={`/comic/${comic.id}`}
                    className="flex items-center gap-4 p-4 rounded-xl bg-background-50 hover:bg-primary-50/50 transition-colors group"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary-500 text-white flex items-center justify-center font-heading text-sm font-bold shrink-0">
                      {index + 1}
                    </div>
                    <div className="w-12 h-16 rounded overflow-hidden shrink-0">
                      <img src={comic.cover} alt={comic.title} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-base text-foreground-950 truncate group-hover:text-primary-500 transition-colors">
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
                    </div>
                    <div className="flex items-center gap-1 text-accent-500 shrink-0">
                      <i className="ri-star-fill text-xs"></i>
                      <span className="text-sm font-medium">{comic.rating}</span>
                    </div>
                    <span className="text-xs text-foreground-400 shrink-0 hidden sm:block">
                      {(comic.views / 10000).toFixed(1)}万 {t('comic.views')}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-14 md:py-20 text-center">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">
              准备好了吗？开启你的韩漫之旅
            </h2>
            <p className="text-sm text-foreground-500 mb-8 max-w-lg mx-auto">
              韩漫漫画网站为你准备了海量免费韩国漫画，无论你喜欢恋爱、奇幻、动作还是悬疑，这里都有最适合你的作品。
            </p>
            <Link
              to="/category/all"
              className="inline-block px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full font-medium text-base transition-all duration-300 whitespace-nowrap"
            >
              立即探索全部漫画
            </Link>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}